// Holistic learner report (Raporlar) — the pure parts: one row model for the five kinds, the ONE
// weighting rule, and the outcome-tree merge Genel durum draws.
//
// The rule (backend contract §0, "question-weighted"): every question counts once, whatever kind it
// came from. Overall = Σ(score × questionCount) / Σ questionCount. A kind the learner has no evidence
// in contributes nothing — never a 0. The server folds the staff report and the destek wizard's
// auto-select with the same rule, so the learner's own tree must use it too or the two disagree.

import { isWithinTimeFilterWindow } from "../library/utilization/DateUtilization";
import { buildCategoryKey } from "../library/utilization/CategoryKeyUtilization";
import { groupMockExamItemsByExam } from "./MockExamReportUtilization";

export const BRANCH_ASSIGNMENT = "BRANCH_ASSIGNMENT";
export const SUPPORT_ASSIGNMENT = "SUPPORT_ASSIGNMENT";
export const PERSONALIZED_ASSIGNMENT = "PERSONALIZED_ASSIGNMENT";
export const HANDWRITTEN_EXAM = "HANDWRITTEN_EXAM";
export const MOCK_EXAM = "MOCK_EXAM";

const isPresentScore = (value: any): value is number =>
  value !== null && value !== undefined && !isNaN(value);

/**
 * How many questions stand behind an item's `learnerScore` — its weight in every overall.
 *
 * <p>Every kind's `learnerScore` is score ÷ TOTAL activities — an unanswered question counts 0 (the PO
 * rule, destek and bireysel included) — so every kind weighs every row it lists. An item that carries
 * a score but no rows (an older payload) still weighs one question rather than vanishing from the
 * average.
 */
export const questionWeightOf = (_kind: string, item: any): number => {
  const detailList: any[] = item?.activityScoreDetailDTOList || [];
  const weight = detailList.length;
  if (weight > 0) return weight;
  return isPresentScore(item?.learnerScore) ? 1 : 0;
};

/** How a row's Başarı % cell reads. */
export type ReportRowScoreState = "SCORED" | "RESULTS_WITHHELD" | "NOT_EVALUATED";

/**
 * A destek whose results are unpublished says so; a destek or bireysel with no score yet (a bireysel
 * not evaluated, a destek nothing graded in) reads "Değerlendirilmedi" — never a 0%, which would read
 * as a fail the learner never had.
 */
export const reportRowScoreStateOf = (row: HolisticReportRow): ReportRowScoreState => {
  if (row.resultsWithheld) return "RESULTS_WITHHELD";
  if (
    row.learnerScore == null &&
    (row.kind === SUPPORT_ASSIGNMENT || row.kind === PERSONALIZED_ASSIGNMENT)
  ) {
    return "NOT_EVALUATED";
  }
  return "SCORED";
};

/** Σ(score × weight) / Σ weight over the rows that HAVE a score; null when none does. */
export const questionWeightedMean = (
  entryList: { score: number | null | undefined; weight: number }[],
): number | null => {
  let weightSum = 0;
  let scoreSum = 0;
  (entryList || []).forEach(({ score, weight }) => {
    if (!isPresentScore(score) || !(weight > 0)) return;
    weightSum += weight;
    scoreSum += score * weight;
  });
  return weightSum > 0 ? scoreSum / weightSum : null;
};

/** One line of the Ödevler / Sınavlar list, whatever kind it came from. */
export interface HolisticReportRow {
  kind: string;
  id: any;
  /** Unique across kinds — a şube ödevi and a destek can share a numeric id. */
  key: string;
  item: any;
  beginAt: number | null;
  endAt: number | null;
  /** The date the row sorts and plots on. */
  sortAt: number | null;
  completionStatus: string | null;
  /** 0..1, or null when the row carries no score yet. */
  learnerScore: number | null;
  /** Şube / sınıf benchmark, 0..1 — always null for destek and bireysel (no comparison exists). */
  branchScore: number | null;
  gradeScore: number | null;
  /** Destek only: the teacher has not published the results yet. */
  resultsWithheld: boolean;
  /** Destek only: before → after. */
  baselineScore: number | null;
  scoreDelta: number | null;
  weight: number;
}

const toNullableScore = (value: any): number | null =>
  isPresentScore(value) ? Number(value) : null;

const sortNewestFirst = (rowList: HolisticReportRow[]) =>
  rowList.sort((a, b) => (b.sortAt ?? -Infinity) - (a.sortAt ?? -Infinity));

const inCoterie = (item: any, coterieType: string | null | undefined) =>
  !coterieType ||
  coterieType === "DEFAULT_OPTION" ||
  item?.coterieType === coterieType;

/**
 * Ödevler: the rows of the selected ödev kinds, merged and newest first.
 *
 * <p>Şube lists its EVALUATED items and bireysel every item — what their own tabs always listed; a
 * bireysel ödev not evaluated yet carries no score into any overall (it is not evidence). Destek lists every
 * item it has — a destek whose results the teacher has not published yet is still the learner's work,
 * and the row says why it carries no number instead of disappearing (or, worse, reading 0%).
 */
export const buildAssignmentReportRowList = (
  source: {
    branchAssignmentItems?: any[] | null;
    supportAssignmentItems?: any[] | null;
    personalizedAssignmentItems?: any[] | null;
  },
  options: { coterieType?: string | null; sourceIds: string[] },
): HolisticReportRow[] => {
  const { coterieType, sourceIds } = options;
  const rowList: HolisticReportRow[] = [];
  if (sourceIds.includes(BRANCH_ASSIGNMENT)) {
    (source.branchAssignmentItems || [])
      .filter((item) => item?.isEvaluated && inCoterie(item, coterieType))
      .forEach((item) =>
        rowList.push({
          kind: BRANCH_ASSIGNMENT,
          id: item.branchAssignmentId,
          key: `${BRANCH_ASSIGNMENT}-${item.branchAssignmentId}`,
          item,
          beginAt: item.beginAt ?? null,
          endAt: item.endAt ?? null,
          sortAt: item.endAt ?? item.createdAt ?? null,
          completionStatus: item.completionStatus ?? null,
          learnerScore: toNullableScore(item.learnerScore),
          branchScore: toNullableScore(item.branchScore),
          gradeScore: toNullableScore(item.gradeScore),
          resultsWithheld: false,
          baselineScore: null,
          scoreDelta: null,
          weight: questionWeightOf(BRANCH_ASSIGNMENT, item),
        }),
      );
  }
  if (sourceIds.includes(SUPPORT_ASSIGNMENT)) {
    (source.supportAssignmentItems || [])
      .filter((item) => item && inCoterie(item, coterieType))
      .forEach((item) => {
        const resultsWithheld = item.resultsPublished === false;
        rowList.push({
          kind: SUPPORT_ASSIGNMENT,
          id: item.supportAssignmentId,
          key: `${SUPPORT_ASSIGNMENT}-${item.supportAssignmentId}`,
          item,
          beginAt: item.beginAt ?? null,
          endAt: item.endAt ?? null,
          sortAt: item.endAt ?? item.createdAt ?? null,
          completionStatus: item.completionStatus ?? null,
          learnerScore: resultsWithheld ? null : toNullableScore(item.learnerScore),
          // No şube / sınıf benchmark exists for destek (hand-picked roster); the contract sends null,
          // and the row says so by drawing none.
          branchScore: null,
          gradeScore: null,
          resultsWithheld,
          baselineScore: toNullableScore(item.baselineScore),
          scoreDelta: resultsWithheld ? null : toNullableScore(item.scoreDelta),
          weight: questionWeightOf(SUPPORT_ASSIGNMENT, item),
        });
      });
  }
  if (sourceIds.includes(PERSONALIZED_ASSIGNMENT)) {
    (source.personalizedAssignmentItems || [])
      .filter((item) => item && inCoterie(item, coterieType))
      .forEach((item) =>
        rowList.push({
          kind: PERSONALIZED_ASSIGNMENT,
          id: item.personalizedAssignmentId,
          key: `${PERSONALIZED_ASSIGNMENT}-${item.personalizedAssignmentId}`,
          item,
          beginAt: item.beginAt ?? null,
          endAt: item.endAt ?? null,
          sortAt: item.endAt ?? item.createdAt ?? null,
          completionStatus: item.completionStatus ?? null,
          learnerScore: item.isEvaluated ? toNullableScore(item.learnerScore) : null,
          // A bireysel ödev has one learner — there is nothing to compare against.
          branchScore: null,
          gradeScore: null,
          resultsWithheld: false,
          baselineScore: null,
          scoreDelta: null,
          weight: questionWeightOf(PERSONALIZED_ASSIGNMENT, item),
        }),
      );
  }
  return sortNewestFirst(rowList);
};

/**
 * Sınavlar: the rows of the selected exam kinds, merged and newest first.
 *
 * <p>Yazılı follows the Ders picker. A deneme spans every ders it tested, so — as before — it ignores
 * the picker and folds the backend's per-(deneme, ders) items into one row per deneme, weighted by
 * ALL of that deneme's questions.
 */
export const buildExamReportRowList = (
  source: {
    handwrittenExamItems?: any[] | null;
    mockExamItems?: any[] | null;
  },
  options: { coterieType?: string | null; sourceIds: string[] },
): HolisticReportRow[] => {
  const { coterieType, sourceIds } = options;
  const rowList: HolisticReportRow[] = [];
  if (sourceIds.includes(HANDWRITTEN_EXAM)) {
    (source.handwrittenExamItems || [])
      .filter((item) => item && inCoterie(item, coterieType))
      .forEach((item, index) =>
        rowList.push({
          kind: HANDWRITTEN_EXAM,
          id: item.examId ?? item.id ?? null,
          key: `${HANDWRITTEN_EXAM}-${item.examId ?? item.id ?? `i${index}`}-${item.coterieType ?? ""}`,
          item,
          beginAt: null,
          endAt: null,
          sortAt: item.createdAt ?? null,
          completionStatus: item.completionStatus ?? null,
          learnerScore: toNullableScore(item.learnerScore),
          branchScore: toNullableScore(item.branchScore),
          gradeScore: toNullableScore(item.gradeScore),
          resultsWithheld: false,
          baselineScore: null,
          scoreDelta: null,
          weight: questionWeightOf(HANDWRITTEN_EXAM, item),
        }),
      );
  }
  if (sourceIds.includes(MOCK_EXAM)) {
    const mockExamItems = (source.mockExamItems || []).filter(Boolean);
    // Keyed exactly as groupMockExamItemsByExam keys its groups (same list, same key, same insertion
    // order), so the i-th weight belongs to the i-th grouped row — an exam without an examId included.
    const weightByExamKey = new Map<string, number>();
    mockExamItems.forEach((item: any, index: number) => {
      const examKey = item.examId != null ? `exam-${item.examId}` : `item-${index}`;
      weightByExamKey.set(
        examKey,
        (weightByExamKey.get(examKey) ?? 0) + questionWeightOf(MOCK_EXAM, item),
      );
    });
    const groupWeightList = Array.from(weightByExamKey.values());
    groupMockExamItemsByExam(mockExamItems).forEach((row: any, index: number) => {
      rowList.push({
        kind: MOCK_EXAM,
        id: row.examId ?? null,
        key: `${MOCK_EXAM}-${row.examId ?? `i${index}`}`,
        item: row,
        beginAt: null,
        endAt: null,
        sortAt: row.createdAt ?? null,
        completionStatus: row.completionStatus ?? null,
        learnerScore: toNullableScore(row.learnerScore),
        branchScore: toNullableScore(row.branchScore),
        gradeScore: toNullableScore(row.gradeScore),
        resultsWithheld: false,
        baselineScore: null,
        scoreDelta: null,
        weight: groupWeightList[index] ?? questionWeightOf(MOCK_EXAM, row),
      });
    });
  }
  return sortNewestFirst(rowList);
};

const meanOf = (
  rowList: HolisticReportRow[],
  field: "learnerScore" | "branchScore" | "gradeScore",
) => questionWeightedMean(rowList.map((row) => ({ score: row[field], weight: row.weight })));

/**
 * The overall rings of a merged list (Ödevler / Sınavlar), question-weighted. The learner is compared
 * with şube / sınıf over the SAME rows: the scored rows that carry a benchmark (branchScore or
 * gradeScore). Destek and bireysel rows carry none, so folding them into the learner ring only would
 * set the learner against a şube figure that never covered that work. When no row carries a benchmark,
 * the learner ring folds every scored row and no comparison ring is drawn (both benchmarks null).
 * Mirrors mobile's `reportRingMeans`.
 */
export const summarizeReportRowList = (rowList: HolisticReportRow[]) => {
  const comparableRowList = (rowList || []).filter(
    (row) =>
      isPresentScore(row.learnerScore) &&
      (isPresentScore(row.branchScore) || isPresentScore(row.gradeScore)),
  );
  if (comparableRowList.length === 0) {
    return { learnerScore: meanOf(rowList || [], "learnerScore"), branchScore: null, gradeScore: null };
  }
  return {
    learnerScore: meanOf(comparableRowList, "learnerScore"),
    branchScore: meanOf(comparableRowList, "branchScore"),
    gradeScore: meanOf(comparableRowList, "gradeScore"),
  };
};

// ---------------------------------------------------------------------------------------------
// Outcome tree merge (Genel durum)
// ---------------------------------------------------------------------------------------------

const nodeKeyOf = (node: any): string =>
  node?.categoryDTO?.id != null
    ? `id:${node.categoryDTO.id}`
    : `key:${buildCategoryKey(node?.categoryDTO)}`;

const scoreRowsOf = (node: any): any[] => {
  const rows = node?.activityScoreDTOSet ?? node?.activityScoreList;
  if (!rows) return [];
  return Array.isArray(rows) ? rows : Array.from(rows);
};

/**
 * Real tree rows are per question (a deneme's too — one row per question), so they weigh 1. Only the
 * synthetic row `absorb` makes for a pre-folded leaf carries `questionCount`.
 */
const rowWeightOf = (row: any): number => {
  const weight = Number(row?.questionCount);
  return Number.isFinite(weight) && weight > 0 ? weight : 1;
};

const naturalCodeCompare = (a: any, b: any) =>
  String(a?.categoryDTO?.code ?? "").localeCompare(
    String(b?.categoryDTO?.code ?? ""),
    undefined,
    { numeric: true },
  );

interface MergeTarget {
  node: any;
  rows: any[];
  children: MergeTarget[];
  childByKey: Map<string, MergeTarget>;
}

const newMergeTarget = (source: any): MergeTarget => ({
  node: source,
  rows: [],
  children: [],
  childByKey: new Map(),
});

const absorb = (
  targetList: MergeTarget[],
  targetByKey: Map<string, MergeTarget>,
  sourceList: any[],
) => {
  (sourceList || []).forEach((source) => {
    if (!source?.categoryDTO) return;
    const key = nodeKeyOf(source);
    let target = targetByKey.get(key);
    if (!target) {
      target = newMergeTarget(source);
      targetByKey.set(key, target);
      targetList.push(target);
    }
    const rows = scoreRowsOf(source);
    if (rows.length > 0) {
      target.rows.push(...rows);
    } else if (
      (source.subCategoryActivityAnswerDTOList || []).length === 0 &&
      (source.aggregatedActivityCount ?? 0) > 0 &&
      isPresentScore(source.aggregatedAveragePercentage)
    ) {
      // A leaf that arrives with aggregates but no rows (a pre-folded node) still weighs its questions.
      target.rows.push({
        activityId: null,
        score: source.aggregatedAveragePercentage / 100,
        questionCount: source.aggregatedActivityCount,
      });
    }
    absorb(target.children, target.childByKey, source.subCategoryActivityAnswerDTOList || []);
  });
};

const finalize = (target: MergeTarget, timeFilter: string): any | null => {
  const subList = target.children
    .map((child) => finalize(child, timeFilter))
    .filter(Boolean)
    .sort(naturalCodeCompare);
  const rows = target.rows.filter(
    (row) =>
      isPresentScore(row?.score) &&
      (!timeFilter ||
        timeFilter === "all" ||
        isWithinTimeFilterWindow(row?.endAt ?? row?.createdAt, timeFilter)),
  );
  let count = 0;
  let sum = 0;
  let highest: number | null = null;
  let lowest: number | null = null;
  rows.forEach((row) => {
    const weight = rowWeightOf(row);
    count += weight;
    sum += row.score * weight;
    const pct = row.score * 100;
    if (highest == null || pct > highest) highest = pct;
    if (lowest == null || pct < lowest) lowest = pct;
  });
  subList.forEach((sub) => {
    count += sub.aggregatedActivityCount ?? 0;
    sum += sub.aggregatedScoreSum ?? 0;
  });
  if (count === 0) return null;
  return {
    ...target.node,
    activityScoreDTOSet: rows,
    activityScoreList: rows,
    subCategoryActivityAnswerDTOList: subList,
    aggregatedActivityCount: count,
    aggregatedScoreSum: sum,
    aggregatedAveragePercentage: (sum / count) * 100,
    aggregatedHighestActivityPercentage: highest,
    aggregatedLowestActivityPercentage: lowest,
  };
};

/**
 * Merges the outcome trees of every enabled kind into ONE tree, then recomputes every node:
 * `percentage = Σ row.score / Σ rows × 100` over the question rows under it (see rowWeightOf). That is
 * the Genel durum merge rule of the backend contract — every question once, blank = 0 — so a node here
 * equals the server's fold PROVIDED the input trees keep their blank rows: the şube, destek, bireysel
 * and yazılı trees are built with `countBlankAnswers` (LearnerApp), pinned end to end by the "blanks
 * count 0 through the builder" tests in HolisticReportUtilization.test.ts.
 *
 * <p>The previous merge concatenated the rows but kept the FIRST source's pre-computed aggregates, so a
 * node read one kind's percentage over every kind's questions. The time window is applied to the rows
 * (each stamped with its item's date) BEFORE the fold, and a node left with no question is dropped.
 */
export const mergeOutcomeReportLists = (
  reportListList: any[][],
  timeFilter: string = "all",
): any[] => {
  const rootList: MergeTarget[] = [];
  const rootByKey = new Map<string, MergeTarget>();
  (reportListList || []).forEach((reportList) =>
    absorb(rootList, rootByKey, reportList || []),
  );
  return rootList
    .map((root) => finalize(root, timeFilter))
    .filter(Boolean)
    .sort(naturalCodeCompare);
};
